import React from "react";
import { Form, Tag } from "antd";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTicketAlt } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  SyncOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import VALIDATE_PROMO_CODE from "graphql/mutations/ValidatePromoCode";

import {
  InlineInput,
  ApplyButton,
  CouponDescription,
  WhenMismatchedCurrency,
} from "./styles";
import "./PromoCode.css";

type TProps = {
  inputVisible: boolean;
  hideInput: () => void;
  showInput: () => void;
  setDiscount: (number: number) => void;
  classPrice: number;
  currency: string;
};
const PROMO_CODE_FIELD_NAME = "promoCode";
const PromoCodeInput = ({
  inputVisible,
  hideInput,
  showInput,
  setDiscount,
  classPrice,
  currency,
}: TProps) => {
  const { t } = useTranslation("registration");
  const [validatePromoCode, { data: promoCodeData }] =
    useMutation(VALIDATE_PROMO_CODE);

  const promoCodeValidator = async (_: any, value: string | undefined) => {
    if (!value) return;

    hideInput();
    try {
      await validatePromoCode({
        variables: {
          promoCode: value,
        },
      });
    } catch (e) {
      // TODO: handling not applicable promo code
      throw new Error(t("Invalid promotion code"));
    }
  };

  // if null currency in validateCoupon, this is a percentOff coupon and matchedCurrency is auto true
  let matchedCurrency = false; // must be false if coupon not valid

  if (promoCodeData?.validateCoupon.coupon.valid) {
    matchedCurrency =
      !promoCodeData?.validateCoupon.coupon.currency ||
      currency === promoCodeData?.validateCoupon?.coupon?.currency;
    if (matchedCurrency) {
      if (promoCodeData.validateCoupon.coupon.amountOff)
        setDiscount(promoCodeData.validateCoupon.coupon.amountOff / 100);
      // stripe doesn't include dot before 2 decimals
      else
        setDiscount(
          (promoCodeData.validateCoupon.coupon.percentOff * classPrice) / 100
        );
    }
  } else {
    setDiscount(0);
  }

  return (
    <div>
      {/* Part 1: Display input */}
      <Form.Item noStyle shouldUpdate>
        {({ validateFields }) => {
          const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
            e.preventDefault();
            hideInput();
            validateFields(["promoCode"]);
          };
          return (
            <>
              <Form.Item
                name="promoCode"
                noStyle
                rules={[{ validator: promoCodeValidator }]}
                validateTrigger={["onBlur"]}
                hidden={!inputVisible}
              >
                <InlineInput
                  prefix={<FontAwesomeIcon icon={faTicketAlt as IconProp} />}
                  type={inputVisible ? "text" : "hidden"}
                  autoFocus
                  placeholder={t("Enter Discount Code")}
                  onPressEnter={handleEnter}
                  size="large"
                  style={{ width: "50%" }}
                />
              </Form.Item>
              {inputVisible && (
                <ApplyButton
                  size="small"
                  onClick={() => {
                    validateFields(["promoCode"]);
                  }}
                >
                  APPLY
                </ApplyButton>
              )}
            </>
          );
        }}
      </Form.Item>
      {/* Part 2: Show tag */}
      <Form.Item noStyle shouldUpdate>
        {({
          getFieldValue,
          setFieldsValue,
          getFieldError,
          isFieldValidating,
        }) => {
          const promoCode: string | undefined = getFieldValue(
            PROMO_CODE_FIELD_NAME
          );
          const errors = getFieldError(PROMO_CODE_FIELD_NAME);
          const validating: boolean = isFieldValidating(PROMO_CODE_FIELD_NAME);
          const handleRemoveCode = () => {
            setFieldsValue({ promoCode: undefined });
            showInput();
          };
          const checkColor = () => {
            if (validating) return "default";
            if (errors.length > 0 || !matchedCurrency) return "warning";
            if (errors.length === 0) return "processing";
            return "default";
          };
          const tagColor = checkColor();
          const wholeTagColor = () => {
            if (tagColor === "default") return "rgb(240, 240, 240)";
            if (tagColor === "warning") return "#fad8b7";
            return "#d3e8ff";
          };
          const renderIcon = () => {
            if (validating)
              return <SyncOutlined style={{ fontSize: "180%" }} />;
            if (errors.length > 0 || !matchedCurrency)
              // either invalid coupon or different currency
              return <ExclamationCircleOutlined style={{ fontSize: "180%" }} />;
            if (errors.length === 0)
              return <CheckCircleOutlined style={{ fontSize: "180%" }} />;
            return null;
          };

          const renderMessage = () => {
            if (validating) return { message: "", color: "gray" };
            if (errors.length > 0) {
              return { message: "Invalid Code", color: "orange" };
            }
            if (!matchedCurrency) {
              return {
                message: "Wrong Currency",
                color: "orange",
              };
            }
            return { message: "Success - Coupon Applied!", color: "#1070b6" };
          };

          if (!inputVisible && promoCode) {
            return (
              <>
                <Tag
                  color={tagColor}
                  closable
                  onClose={() => handleRemoveCode()}
                  icon={renderIcon()}
                  style={{
                    height: "50px",
                    width: "45%",
                    backgroundColor: wholeTagColor(),
                  }}
                >
                  {promoCode}
                </Tag>
                <CouponDescription
                  style={{ color: renderMessage().color, width: "45%" }}
                >
                  {renderMessage().message}
                </CouponDescription>
                {promoCodeData?.validateCoupon.coupon.valid &&
                !matchedCurrency ? (
                  <WhenMismatchedCurrency>
                    The discount and your account currency do not match. To use
                    this discount code, register a new account with a
                    subscription for that currency.
                  </WhenMismatchedCurrency>
                ) : null}
              </>
            );
          }
          return null;
        }}
      </Form.Item>
    </div>
  );
};

export default PromoCodeInput;
